import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpStatusCode
} from '@angular/common/http';
import {inject} from '@angular/core';
import {tap} from 'rxjs/operators';
import {get} from 'lodash';
import {SnackBarService, retryWithBackoff, ConfigService} from '@q9elements/ui-kit/common';
import {
  Q9PKServerErrorResponse,
  TypedHttpErrorResponse
} from './shared/models/pk-server-error-response.interface';
import {environment} from 'src/environments/environment';
import {AuthService} from './core/auth/auth.service';
import {PopupManagerService} from './core/services/popup-manager.service';
import {
  AUTHORISATION_ERRORS,
  DIAGRAM_PERMISSION_ERRORS,
  DIAGRAM_STATE_ERRORS,
  LICENSE_ERRORS,
  MAP_PERMISSION_ERRORS,
  MAP_STATE_ERRORS,
  ORGANIZATION_STATE_ERRORS,
  USER_STATE_ERRORS
} from './core/values/pk-server-errors';

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const authService = inject(AuthService);
  const snackbarService = inject(SnackBarService);
  const popupsService = inject(PopupManagerService);
  const initialLoad = true;
  let headers = req.headers;
  const teamIndex = authService.getTeamIndex();
  const token = req.headers.get('Authorization');
  const whitelistUrl =
    req.url.indexOf(environment.API_ENDPOINT) > -1 ||
    req.url.indexOf(environment.REPORTS_API_ENDPOINT) > -1 ||
    req.url.indexOf(environment.API_STATIC_FILE_UPLOAD) > -1;

  if (whitelistUrl && token !== 'no_token') {
    if (teamIndex === null) {
      headers = req.headers.set('Authorization', `token ${authService.getToken()}`);
    } else {
      headers = req.headers
        .set('Authorization', `token ${authService.getToken()}`)
        .set('el-team', teamIndex.toString());
    }
  } else {
    headers = req.headers.delete('Authorization');
  }

  const authReq = req.clone({headers: headers});

  return next(authReq).pipe(
    retryWithBackoff(),
    tap({
      error: (err: any) => {
        if (!navigator.onLine) {
          return snackbarService.error('POPUPS.NO_INTERNET');
        }

        if (err instanceof HttpErrorResponse) {
          switch (err.status) {
            case HttpStatusCode.Forbidden:
              handleErrorStatus(err, popupsService, false, initialLoad);
              break;

            case HttpStatusCode.Unauthorized: {
              const redirectTo = encodeURI(window.location.href);

              window.location.href =
                environment.MAIN_APP_URL +
                '/signin?redirect=' +
                redirectTo.replace(/\?/g, '%3F').replace(/&/g, '%26');
              break;
            }

            case HttpStatusCode.BadRequest: {
              if (err.error.status === DIAGRAM_STATE_ERRORS.ATTACHMENT_WRONG_FORMAT) {
                handleErrorStatus(err, popupsService, false, initialLoad);
              }

              if (err.error.status === USER_STATE_ERRORS.NOT_PRO_INFO) {
                handleErrorStatus(err, popupsService, false, initialLoad);
              }

              const {ELEMENT_NOT_EXISTS, VALIDATION_FAILED, KEY_ALREADY_EXISTS} =
                DIAGRAM_STATE_ERRORS;

              if (
                [ELEMENT_NOT_EXISTS, VALIDATION_FAILED, KEY_ALREADY_EXISTS].find(
                  status => status === err.error.status
                )
              ) {
                handleErrorStatus(err, popupsService, false, initialLoad);
              }

              break;
            }

            case HttpStatusCode.NotFound:
              if (err.error.status === DIAGRAM_STATE_ERRORS.VERSION_RESTRICTED) {
                handleErrorStatus(err, popupsService, false, initialLoad);
              }

              break;

            case HttpStatusCode.InternalServerError: {
              handleErrorStatus(err, popupsService, true, initialLoad);
            }
          }
        }
      }
    })
  );
};

function handleErrorStatus(
  error: TypedHttpErrorResponse<Q9PKServerErrorResponse>,
  popupSvc: PopupManagerService,
  serverError = false,
  initialLoad: boolean
): boolean | TypedHttpErrorResponse<Q9PKServerErrorResponse> {
  if (serverError) {
    popupSvc.openErrorDialog({
      message: 'POPUPS.DRAW_NOT_SAVED',
      callback: 'reloadToViewMode',
      disableClose: true
    });

    return true;
  }

  const status = error.error.status;
  const parts = error.url.split('/');
  const idIndex = parts.indexOf('diagram') + 1;

  switch (status) {
    case DIAGRAM_STATE_ERRORS.VERSION_RESTRICTED: {
      if (initialLoad) {
        initialLoad = false;

        return true;
      }

      const errorMessage = get(error, 'error.error', 'POPUPS.NO_ACCESS');

      popupSvc.openErrorDialog({message: errorMessage});

      return true;
    }

    case DIAGRAM_STATE_ERRORS.VALIDATION_FAILED:

    case DIAGRAM_STATE_ERRORS.ELEMENT_NOT_EXISTS:

    case DIAGRAM_STATE_ERRORS.KEY_ALREADY_EXISTS:
      popupSvc.openErrorDialog({
        message: 'POPUPS.DRAW_NOT_SAVED',
        callback: 'reloadToViewMode',
        disableClose: true
      });

      return true;

    case MAP_PERMISSION_ERRORS.DOES_NOT_EXIST:

    case ORGANIZATION_STATE_ERRORS.NOT_FOUND:
      if (idIndex > 0 && idIndex < parts.length) {
        const diagramId = parts[idIndex];

        window.location.href = `${ConfigService.environment.MAIN_APP_URL}/request-access/${diagramId}`;
      }

      return true;

    case MAP_PERMISSION_ERRORS.IS_PERSONAL:
      if (idIndex > 0 && idIndex < parts.length) {
        window.location.href = `${ConfigService.environment.MAIN_APP_URL}/request-access/error_personal`;
      }

      return true;

    case USER_STATE_ERRORS.USER_BLOCKED_IN_TEAM:

    case USER_STATE_ERRORS.USER_BLOCKED:

    case USER_STATE_ERRORS.USER_NOT_VERIFIED:

    case USER_STATE_ERRORS.DELETED_BY_SYSTEM:
      if (/children/.test(error.url)) {
        return error;
      }

      popupSvc.openErrorDialog({
        message: 'POPUPS.NO_ACCESS',
        callback: 'navigateToDashboard'
      });

      return true;

    case MAP_PERMISSION_ERRORS.CANNOT_VIEW:
      popupSvc.openErrorDialog({
        message: 'POPUPS.NO_ACCESS',
        callback: 'navigateToDashboard'
      });

      return true;

    case MAP_STATE_ERRORS.DELETED:
      popupSvc.openErrorDialog({
        message: 'POPUPS.NO_EDIT_DELETED',
        callback: 'reloadToViewMode'
      });

      return true;

    case MAP_PERMISSION_ERRORS.CANNOT_EDIT:

    case DIAGRAM_PERMISSION_ERRORS.CANNOT_EDIT:
      popupSvc.openErrorDialog({
        message: 'POPUPS.NO_EDIT_ACCESS',
        callback: 'reloadToViewMode',
        disableClose: true,
        navWithoutReload: true
      });

      return true;

    case DIAGRAM_PERMISSION_ERRORS.CANNOT_EDIT_NOTES:
      popupSvc.openErrorDialog({message: error.error.error, callback: 'reloadToViewMode'});

      return true;

    case DIAGRAM_STATE_ERRORS.LOCKED:
      popupSvc.openErrorDialog({message: 'POPUPS.LOCKED', callback: 'reloadToViewMode'});

      return true;

    case USER_STATE_ERRORS.ACCESS_DENIED:
      popupSvc.openErrorDialog({
        message: 'POPUPS.NO_EDIT_ACCESS',
        callback: 'navigateToDashboard'
      });

      return true;

    case USER_STATE_ERRORS.NOT_PRO:

    case USER_STATE_ERRORS.NOT_PRO_INFO:
      popupSvc.openErrorDialog({
        message: 'POPUPS.NOT_PRO_VERSION',
        callback: 'navigateToDashboard'
      });

      return true;

    case ORGANIZATION_STATE_ERRORS.SUSPENDED:
      popupSvc.openErrorDialog({
        message: 'POPUPS.TEAM_SUSPENDED',
        callback: 'navigateToDashboard'
      });

      return true;

    case DIAGRAM_STATE_ERRORS.ATTACHMENT_WRONG_FORMAT:
      popupSvc.openErrorDialog({
        message: 'POPUPS.AFTER_RELOAD',
        callback: 'reloadToViewMode'
      });

      return true;

    case DIAGRAM_PERMISSION_ERRORS.NOT_REQUIREMENT_MANAGER:
      popupSvc.closeAll(true);
      popupSvc.openErrorDialog({
        message: 'POPUPS.REQUIREMENT_MANAGER_RIGHTS',
        navWithoutReload: true
      });

      return true;

    case AUTHORISATION_ERRORS.TWO_FACTOR_REQUIRED: {
      const gracePeriodExpired = get(error, 'error.gracePeriodExpired', false);
      const gracePeriodNotified = get(error, 'error.gracePeriodNotified', false);

      if (gracePeriodExpired || !gracePeriodNotified) {
        popupSvc
          .openTwoFaRequiredDialog(gracePeriodExpired)
          .subscribe(() => window.location.reload());
      }

      return true;
    }

    case LICENSE_ERRORS.LICENSE_REQUIRED: {
      const errorMessage = get(error, 'error.error');

      popupSvc.closeAll(true);
      popupSvc
        .openErrorDialog({message: errorMessage})
        .afterClosed()
        .subscribe(() => (window.location.href = `${ConfigService.environment.MAIN_APP_URL}`));

      return true;
    }
  }
}
